import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { AbstractControl, FormArray, FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatIconModule } from '@angular/material/icon';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { MatDialog } from '@angular/material/dialog';
import { catchError, forkJoin, Observable, of, switchMap, take } from 'rxjs';
import { MainHeaderComponent } from '../../shared/main-header/main-header.component';
import { SearchAutocompleteComponent } from '../../shared/search-autocomplete/search-autocomplete.component';
import {
  DatasetSchema,
  SchemaColumn,
  SchemaPermission,
  SchemasService
} from '../../../services/schemas.service';
import {
  BulkColumnInput,
  SchemaColumnsJsonDialogComponent
} from '../schema-columns-json-dialog/schema-columns-json-dialog.component';
import { UsersService } from '../../../users/users.service';
import { AuthService } from '../../../services/auth.service';
import { readSheet } from 'read-excel-file/browser';

type SchemaPermissionVM = {
  uid: string;
  label: string;
};

const DATA_TYPE_HINTS: Record<string, string> = {
  text: 'ej: Juan Pérez',
  integer: 'ej: 42',
  decimal: 'ej: 19.99',
  boolean: 'ej: true o false',
  date: 'ej: 2024-01-31',
  timestamp: 'ej: 2024-01-31 14:30',
  json: 'ej: {"clave": "valor"}'
};

@Component({
  selector: 'app-schemas',
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    ReactiveFormsModule,
    MatButtonModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatCheckboxModule,
    MatIconModule,
    MatExpansionModule,
    MatSnackBarModule,
    MainHeaderComponent,
    SearchAutocompleteComponent
  ],
  templateUrl: './schemas.component.html',
  styleUrl: './schemas.component.scss'
})
export class SchemasComponent implements OnInit {
  schemaId: string | null = null;
  isEditMode = false;
  isSaving = false;
  isLoading = false;

  usersCatalog: Array<{ uid: string; name?: string; email?: string }> = [];
  usersSearchItems: Array<{ uid: string; name: string; description: string }> = [];
  selectedUserUid = '';

  dataTypeOptions = [
    { value: 'text',      label: 'text' },
    { value: 'integer',   label: 'integer' },
    { value: 'decimal',   label: 'decimal' },
    { value: 'boolean',   label: 'boolean' },
    { value: 'date',      label: 'date' },
    { value: 'timestamp', label: 'timestamp' },
    { value: 'json',      label: 'json' }
  ];

  showDestinationAdvanced = false;
  expandedConstraints = new Set<number>();
  selectedColumnIndex = 0;
  formSubmitted = false;
  private targetTableManuallyEdited = false;
  private readonly manuallyEditedColumnNames = new WeakSet<FormGroup>();

  private readonly dataTypeIcons: Record<string, string> = {
    text: 'notes',
    integer: 'tag',
    decimal: 'percent',
    boolean: 'toggle_on',
    date: 'event',
    timestamp: 'schedule',
    json: 'code'
  };

  form = this.fb.group({
    name: ['', [Validators.required]],
    description: [''],
    destination_type: ['postgres', [Validators.required]],
    target_schema: ['public', [Validators.required]],
    target_table: ['', [Validators.required]],
    columns: this.fb.array([]),
  });

  permissions: SchemaPermissionVM[] = [];
  originalPermissionUids = new Set<string>();

  constructor(
    private fb: FormBuilder,
    private route: ActivatedRoute,
    private router: Router,
    private snackBar: MatSnackBar,
    private schemasService: SchemasService,
    private usersService: UsersService,
    private authService: AuthService,
    private dialog: MatDialog
  ) { }

  ngOnInit(): void {
    this.schemaId = this.route.snapshot.paramMap.get('id');
    this.isEditMode = !!this.schemaId;

    this.addColumn();
    this.loadUsersCatalog();

    this.columnsArray.valueChanges.subscribe(() => this.revalidateColumns());

    if (!this.isEditMode || !this.schemaId) return;
    this.loadSchemaForEdit(this.schemaId);
  }

  get pageTitle(): string {
    if (this.isEditMode) {
      const schemaName = String(this.form.get('name')?.value ?? '').trim();
      return schemaName ? `Editar modelo: ${schemaName}` : 'Editar modelo de datos';
    }
    return 'Crear modelo de datos';
  }

  get columnsArray(): FormArray {
    return this.form.get('columns') as FormArray;
  }

  onNameInput(): void {
    if (this.targetTableManuallyEdited) return;
    const nameValue = String(this.form.get('name')?.value ?? '');
    this.form.get('target_table')!.setValue(this.slugify(nameValue), { emitEvent: false });
  }

  onTargetTableInput(): void {
    this.targetTableManuallyEdited = true;
  }

  onColumnNameInput(index: number): void {
    const group = this.columnsArray.at(index) as FormGroup | undefined;
    if (group) this.manuallyEditedColumnNames.add(group);
  }

  addColumn(): void {
    this.columnsArray.push(this.createColumnGroup());
    this.selectedColumnIndex = this.columnsArray.length - 1;
  }

  removeColumn(index: number): void {
    const group = this.columnsArray.at(index) as FormGroup;
    const hasData = !!String(group.get('label')?.value ?? '').trim() || !!String(group.get('name')?.value ?? '').trim();

    this.columnsArray.removeAt(index);
    this.shiftExpandedConstraints(index);
    this.selectedColumnIndex = Math.min(this.selectedColumnIndex, this.columnsArray.length - 1);
    this.selectedColumnIndex = Math.max(this.selectedColumnIndex, 0);

    if (!hasData) return;

    const snackRef = this.snackBar.open('Columna eliminada.', 'Deshacer', { duration: 5000 });
    snackRef.onAction().subscribe(() => {
      this.columnsArray.insert(index, group);
      this.selectedColumnIndex = index;
    });
  }

  selectColumn(index: number): void {
    this.selectedColumnIndex = index;
  }

  prevColumn(): void {
    if (this.selectedColumnIndex > 0) this.selectedColumnIndex--;
  }

  nextColumn(): void {
    if (this.selectedColumnIndex < this.columnsArray.length - 1) this.selectedColumnIndex++;
  }

  columnTypeIcon(index: number): string {
    return this.dataTypeIcons[this.getDataType(index)] ?? 'notes';
  }

  toggleConstraints(index: number): void {
    if (this.expandedConstraints.has(index)) {
      this.expandedConstraints.delete(index);
    } else {
      this.expandedConstraints.add(index);
    }
  }

  isConstraintExpanded(index: number): boolean {
    return this.expandedConstraints.has(index);
  }

  dataTypeHint(index: number): string {
    return DATA_TYPE_HINTS[this.getDataType(index)] ?? '';
  }

  addPermissionUser(): void {
    const uid = this.selectedUserUid?.trim();
    if (!uid) return;

    if (this.permissions.some((p) => p.uid === uid)) {
      this.snackBar.open('Ese usuario ya fue agregado al modelo de datos.', 'Cerrar', {
        duration: 3500
      });
      return;
    }

    const user = this.usersCatalog.find((u) => u.uid === uid);
    const label = user?.name || user?.email || uid;

    this.permissions.push({
      uid,
      label
    });

    this.selectedUserUid = '';
  }

  onUserSelected(item: { uid: string }): void {
    this.selectedUserUid = String(item?.uid ?? '');
    this.addPermissionUser();
  }

  removePermissionUser(uid: string): void {
    this.permissions = this.permissions.filter((p) => p.uid !== uid);
  }

  saveSchema(): void {
    if (this.form.invalid || this.isSaving) {
      this.form.markAllAsTouched();
      this.formSubmitted = true;
      this.expandInvalidColumns();
      this.snackBar.open('Hay campos incompletos o con errores. Revisalos antes de guardar.', 'Cerrar', {
        duration: 4000
      });
      setTimeout(() => this.scrollToFirstInvalid(), 0);
      return;
    }

    this.isSaving = true;
    const payload = this.buildSchemaPayload();
    if (this.isEditMode && this.schemaId) {
      this.schemasService
        .updateSchema$(this.schemaId, payload)
        .pipe(switchMap(() => this.syncPermissions$(this.schemaId!)))
        .subscribe({
          next: () => {
            this.isSaving = false;
            this.snackBar.open('Modelo de datos actualizado correctamente.', 'OK', {
              duration: 3000
            });
          },
          error: () => {
            this.isSaving = false;
            this.snackBar.open('No se pudo actualizar el modelo de datos.', 'Cerrar', {
              duration: 4000
            });
          }
        });
      return;
    }

    this.schemasService
      .createSchema$(payload)
      .pipe(
        switchMap((newId) =>
          this.schemasService.updateSchema$(newId, { id: newId } as Partial<DatasetSchema>).pipe(
            switchMap(() => this.syncPermissions$(newId)),
            switchMap(() => of(newId))
          )
        )
      )
      .subscribe({
        next: (newId) => {
          this.isSaving = false;
          this.snackBar.open('Modelo de datos creado correctamente.', 'OK', {
            duration: 3000
          });
          this.router.navigate(['/schemas', newId, 'edit']);
        },
        error: () => {
          this.isSaving = false;
          this.snackBar.open('No se pudo crear el modelo de datos.', 'Cerrar', {
            duration: 4000
          });
        }
      });
  }

  deleteSchema(): void {
    if (!this.schemaId) return;
    if (!confirm('¿Eliminar este modelo de datos?')) return;

    this.schemasService.deleteSchema$(this.schemaId).subscribe({
      next: () => {
        this.snackBar.open('Modelo de datos eliminado correctamente.', 'OK', {
          duration: 3000
        });
        this.router.navigate(['/schemas']);
      },
      error: () => {
        this.snackBar.open('No se pudo eliminar el modelo de datos.', 'Cerrar', {
          duration: 4000
        });
      }
    });
  }

  private createColumnGroup(initial?: Partial<SchemaColumn>): FormGroup {
    const suggestedName = initial?.label ? this.slugify(initial.label) : '';
    const name = initial?.name ?? suggestedName;
    const group = this.fb.group({
      label:         [initial?.label ?? '', [Validators.required]],
      name:          [name, [Validators.required, Validators.pattern(/^[a-z][a-z0-9_]*$/)]],
      data_type:     [initial?.data_type ?? 'text', [Validators.required]],
      required:      [initial?.required ?? false],
      unique:        [!!initial?.unique],
      description:   [initial?.description ?? ''],
      example:       [initial?.example ?? ''],
      default_value: [initial?.default_value ?? ''],
      max_length:    [initial?.max_length ?? null],
      min_value:     [initial?.min_value ?? null],
      max_value:     [initial?.max_value ?? null],
      precision:     [initial?.precision ?? null],
      scale:         [initial?.scale ?? null],
      enum_values:   [(initial?.enum_values ?? []).join(', ')],
      regex:         [initial?.regex ?? ''],
      date_format:   [initial?.date_format ?? ''],
    });

    if (initial?.name && initial.name !== suggestedName) {
      this.manuallyEditedColumnNames.add(group);
    }

    group.get('label')!.valueChanges.subscribe((value) => {
      if (this.manuallyEditedColumnNames.has(group)) return;
      group.get('name')!.setValue(this.slugify(String(value ?? '')), { emitEvent: false });
    });

    return group;
  }

  private revalidateColumns(): void {
    const groups = this.columnsArray.controls as FormGroup[];

    const labelCounts = new Map<string, number>();
    const nameCounts = new Map<string, number>();
    groups.forEach((group) => {
      const label = String(group.get('label')?.value ?? '').trim().toLowerCase();
      const name = String(group.get('name')?.value ?? '').trim().toLowerCase();
      if (label) labelCounts.set(label, (labelCounts.get(label) ?? 0) + 1);
      if (name) nameCounts.set(name, (nameCounts.get(name) ?? 0) + 1);
    });

    groups.forEach((group) => {
      this.applySyntheticError(group.get('label')!, labelCounts, 'duplicateLabel');
      this.applySyntheticError(group.get('name')!, nameCounts, 'duplicateName');

      const min = group.get('min_value')?.value;
      const max = group.get('max_value')?.value;
      const hasRange = min !== null && min !== '' && max !== null && max !== '' && !isNaN(Number(min)) && !isNaN(Number(max));
      const invalidRange = hasRange && Number(min) > Number(max);
      this.setSyntheticError(group.get('max_value')!, 'rangeInvalid', invalidRange);
    });
  }

  private applySyntheticError(control: AbstractControl, counts: Map<string, number>, errorKey: string): void {
    const value = String(control.value ?? '').trim().toLowerCase();
    const isDuplicate = !!value && (counts.get(value) ?? 0) > 1;
    this.setSyntheticError(control, errorKey, isDuplicate);
  }

  private setSyntheticError(control: AbstractControl, errorKey: string, hasError: boolean): void {
    const currentErrors = { ...(control.errors ?? {}) };
    if (hasError) {
      if (currentErrors[errorKey]) return;
      currentErrors[errorKey] = true;
    } else {
      if (!currentErrors[errorKey]) return;
      delete currentErrors[errorKey];
    }
    control.setErrors(Object.keys(currentErrors).length ? currentErrors : null);
  }

  private expandInvalidColumns(): void {
    let firstInvalid = -1;
    this.columnsArray.controls.forEach((group, i) => {
      if (group.invalid) {
        this.expandedConstraints.add(i);
        if (firstInvalid === -1) firstInvalid = i;
      }
    });
    if (firstInvalid !== -1) this.selectedColumnIndex = firstInvalid;
  }

  private scrollToFirstInvalid(): void {
    const invalidEl = document.querySelector('.schema-form .ng-invalid') as HTMLElement | null;
    invalidEl?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }

  private shiftExpandedConstraints(removedIndex: number): void {
    const shifted = new Set<number>();
    this.expandedConstraints.forEach((i) => {
      if (i === removedIndex) return;
      shifted.add(i > removedIndex ? i - 1 : i);
    });
    this.expandedConstraints = shifted;
  }

  private slugify(value: string): string {
    return value
      .replace(/ñ/g, 'ni')
      .replace(/Ñ/g, 'Ni')
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '_')
      .replace(/^_+|_+$/g, '')
      .replace(/_{2,}/g, '_');
  }

  private buildSchemaPayload(): Omit<DatasetSchema, 'id'> {
    const formValue = this.form.value;
    return {
      name: String(formValue.name ?? '').trim(),
      description: String(formValue.description ?? '').trim(),
      destinations: [
        {
          type: String(formValue.destination_type ?? 'postgres').trim() || 'postgres',
          target: {
            schema: String(formValue.target_schema ?? '').trim(),
            table: String(formValue.target_table ?? '').trim()
          }
        }
      ],
      columns: (formValue.columns ?? []).map((col: any): SchemaColumn => {
        const base: SchemaColumn = {
          name:      String(col.name      ?? '').trim(),
          label:     String(col.label     ?? '').trim(),
          data_type: String(col.data_type ?? '').trim(),
          required:  !!col.required,
        };
        if (col.unique)                                    base.unique        = true;
        if (String(col.description ?? '').trim())          base.description   = String(col.description).trim();
        if (String(col.example     ?? '').trim())          base.example       = String(col.example).trim();
        if (String(col.default_value ?? '').trim())        base.default_value = String(col.default_value).trim();
        if (String(col.date_format ?? '').trim())          base.date_format   = String(col.date_format).trim();
        if (String(col.regex       ?? '').trim())          base.regex         = String(col.regex).trim();
        if (col.max_length  != null && col.max_length  !== '') base.max_length  = Number(col.max_length);
        if (col.min_value   != null && col.min_value   !== '') base.min_value   = Number(col.min_value);
        if (col.max_value   != null && col.max_value   !== '') base.max_value   = Number(col.max_value);
        if (col.precision   != null && col.precision   !== '') base.precision   = Number(col.precision);
        if (col.scale       != null && col.scale       !== '') base.scale       = Number(col.scale);
        if (String(col.enum_values ?? '').trim()) {
          base.enum_values = String(col.enum_values).split(',').map((v: string) => v.trim()).filter(Boolean);
        }
        return base;
      })
    };
  }

  private loadSchemaForEdit(schemaId: string): void {
    this.isLoading = true;

    forkJoin({
      schema: this.schemasService.getSchemaById$(schemaId),
      permissions: this.schemasService.getSchemaPermissions$(schemaId).pipe(
        catchError(() => of([]))
      )
    }).subscribe({
      next: ({ schema, permissions }) => {
        this.isLoading = false;
        if (!schema) return;

        const targetTable = schema.destinations?.[0]?.target?.table ?? '';

        this.form.patchValue({
          name: schema.name ?? '',
          description: schema.description ?? '',
          destination_type: schema.destinations?.[0]?.type ?? 'postgres',
          target_schema: schema.destinations?.[0]?.target?.schema ?? 'public',
          target_table: targetTable
        });
        this.targetTableManuallyEdited = !!targetTable;

        const schemaColumns = this.resolveSchemaColumns(schema);
        this.columnsArray.clear();
        schemaColumns.forEach((col) => {
          this.columnsArray.push(this.createColumnGroup(col));
        });
        if (!this.columnsArray.length) this.addColumn();
        this.selectedColumnIndex = 0;

        this.permissions = (permissions ?? []).map((permission) => ({
          uid: permission.uid,
          label: this.resolveUserLabel(permission.uid)
        }));

        this.originalPermissionUids = new Set(this.permissions.map((p) => p.uid));
      },
      error: () => {
        this.isLoading = false;
        this.snackBar.open('No se pudo cargar el modelo de datos.', 'Cerrar', {
          duration: 4000
        });
      }
    });
  }

  private syncPermissions$(schemaId: string): Observable<unknown> {
    const ensureOwnerPermission$ = this.authService.userData$.pipe(
      take(1),
      switchMap((user) => {
        if (!user?.uid) return of(null);
        const exists = this.permissions.some((p) => p.uid === user.uid);
        if (!exists) {
          this.permissions.push({
            uid: user.uid,
            label: user.name || user.email || user.uid
          });
        }
        return of(user);
      })
    );

    return ensureOwnerPermission$.pipe(
      switchMap(() => {
        const upserts = this.permissions.map((permission) =>
          this.schemasService.upsertSchemaPermission$(schemaId, {
            uid: permission.uid,
            schema_id: schemaId
          } as SchemaPermission)
        );

        const toDelete = [...this.originalPermissionUids].filter(
          (uid) => !this.permissions.some((p) => p.uid === uid)
        );

        const deletes = toDelete.map((uid) =>
          this.schemasService.removeSchemaPermission$(schemaId, uid)
        );

        if (!upserts.length && !deletes.length) {
          return of(true);
        }

        return forkJoin([...upserts, ...deletes]);
      })
    );
  }

  private loadUsersCatalog(): void {
    this.usersService.loadUsersStore();
    this.usersService.users$.subscribe((users: any[]) => {
      this.usersCatalog = (users ?? []).map((u: any) => ({
        uid: String(u.uid ?? ''),
        name: u.name ? String(u.name) : undefined,
        email: u.email ? String(u.email) : undefined
      }));
      this.usersSearchItems = this.usersCatalog.map((user) => ({
        uid: user.uid,
        name: user.email || user.uid,
        description: ''
      }));

      this.permissions = this.permissions.map((permission) => ({
        ...permission,
        label: this.resolveUserLabel(permission.uid)
      }));
    });
  }

  private resolveUserLabel(uid: string): string {
    const user = this.usersCatalog.find((u) => u.uid === uid);
    return user?.name || user?.email || uid;
  }

  openColumnsJsonDialog(): void {
    const dialogRef = this.dialog.open(SchemaColumnsJsonDialogComponent, {
      width: '980px',
      maxWidth: '96vw',
      autoFocus: false
    });

    dialogRef.afterClosed().subscribe((columns?: BulkColumnInput[]) => {
      if (!columns?.length) return;

      this.columnsArray.clear();
      columns.forEach((col) => {
        this.columnsArray.push(this.createColumnGroup({
          label: col.label,
          name: col.name,
          data_type: col.type || 'text',
          required: !col.optional
        }));
      });
      this.selectedColumnIndex = 0;

      this.snackBar.open(
        `Se cargaron ${columns.length} columnas desde JSON.`,
        'OK',
        { duration: 3000 }
      );
    });
  }

  async onSampleFileSelected(event: Event): Promise<void> {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0] ?? null;
    input.value = '';
    if (!file) return;

    const extension = file.name.split('.').pop()?.toLowerCase();
    if (extension !== 'csv' && extension !== 'xlsx') {
      this.snackBar.open('Solo se admiten archivos .csv o .xlsx.', 'Cerrar', { duration: 4000 });
      return;
    }

    const hasExistingData = this.columnsArray.controls.some(
      (group) => !!String(group.get('label')?.value ?? '').trim()
    );
    if (hasExistingData && !confirm('Esto va a reemplazar las columnas actuales por las del archivo. ¿Continuar?')) {
      return;
    }

    try {
      const headers = extension === 'csv'
        ? await this.readCsvHeaderRow(file)
        : await this.readXlsxHeaderRow(file);

      if (!headers.length) {
        this.snackBar.open('No se encontraron columnas en la primera fila del archivo.', 'Cerrar', {
          duration: 4000
        });
        return;
      }

      this.columnsArray.clear();
      headers.forEach((header) => {
        this.columnsArray.push(this.createColumnGroup({ label: header, required: false }));
      });
      this.selectedColumnIndex = 0;

      this.snackBar.open(`Se cargaron ${headers.length} columnas desde el archivo.`, 'OK', { duration: 3000 });
    } catch {
      this.snackBar.open('No se pudo leer el archivo. Probá con otro archivo de ejemplo.', 'Cerrar', {
        duration: 4000
      });
    }
  }

  private readCsvHeaderRow(file: File): Promise<string[]> {
    return file.text().then((text) => {
      const firstLine = text.split(/\r?\n/).find((line) => line.trim().length > 0) ?? '';
      const delimiter = firstLine.includes(';') && !firstLine.includes(',') ? ';' : ',';
      return firstLine
        .split(delimiter)
        .map((cell) => cell.trim().replace(/^"|"$/g, ''))
        .filter(Boolean);
    });
  }

  private async readXlsxHeaderRow(file: File): Promise<string[]> {
    const rows = await readSheet(file);
    const headerRow = rows.find((row) => row.some((cell) => String(cell ?? '').trim().length > 0)) ?? [];
    return headerRow.map((cell) => String(cell ?? '').trim()).filter(Boolean);
  }

  private resolveSchemaColumns(schema: DatasetSchema): SchemaColumn[] {
    return (schema.columns?.length ? schema.columns : schema.schema) ?? [];
  }

  getDataType(columnIndex: number): string {
    return String(this.columnsArray.at(columnIndex)?.get('data_type')?.value ?? '').trim().toLowerCase();
  }

  isDateColumn(i: number): boolean {
    const t = this.getDataType(i);
    return t === 'date' || t === 'timestamp';
  }

  isTextColumn(i: number): boolean {
    return this.getDataType(i) === 'text';
  }

  isNumericColumn(i: number): boolean {
    const t = this.getDataType(i);
    return t === 'integer' || t === 'decimal';
  }

  isDecimalColumn(i: number): boolean {
    return this.getDataType(i) === 'decimal';
  }

}
